import { getActiveTimeZone } from './dates';

/* =============================================================================
 * How this company reads a clock
 * -----------------------------------------------------------------------------
 * Every time in the app was printed as 17:18, because that is what the code
 * happened to ask for — thirteen call sites, each passing `hour12: false` on
 * its own. Nobody chose it, and for a reader who does not think in 24-hour time
 * "17:18" needs arithmetic doing to it before it means five past six in the
 * evening. On an attendance screen, where the whole question is when somebody
 * arrived, that is a real cost.
 *
 * So it becomes a company setting, beside the date format and the timezone, and
 * every clock in the app follows it.
 *
 * -----------------------------------------------------------------------------
 * Three separate settings that keep getting confused
 * -----------------------------------------------------------------------------
 *   timezone     decides WHICH DAY it is, and what hour it is in Ajman
 *   date format  decides how a day, once decided, is written down
 *   time format  decides how an hour, once decided, is written down
 *
 * A company can keep Asia/Dubai, write dates the American way and read its
 * clocks in 24-hour, and none of those choices affects the others or changes a
 * single minute anybody worked.
 *
 * -----------------------------------------------------------------------------
 * Why not toLocaleTimeString with a locale
 * -----------------------------------------------------------------------------
 * Because a locale is not a format. 'en-AE' gives whatever that browser's ICU
 * decided this year, which is how the same screen prints two different times on
 * two machines in one office. The zone is passed explicitly and the 12/24 rule
 * is passed explicitly, so the answer is the same everywhere.
 * ========================================================================== */

export type TimeFormatId = '24h' | '12h';

export const DEFAULT_TIME_FORMAT: TimeFormatId = '24h';

/**
 * Narrows a value read from the database.
 *
 * The column is text and this is a fixed list, so a value that is not on the
 * list has to resolve to the default rather than be passed along as though it
 * were valid. Same shape as isLanguage.
 */
export function isTimeFormat(value: unknown): value is TimeFormatId {
  return value === '24h' || value === '12h';
}

interface TimeFormat {
  id: TimeFormatId;
  label: string;
  /** The same moment in this shape, so nobody has to decode a description. */
  example: string;
}

export const TIME_FORMATS: TimeFormat[] = [
  { id: '24h', label: 'Twenty-four hour', example: '17:18' },
  { id: '12h', label: 'Twelve hour, am and pm', example: '5:18 pm' },
];

/* The company's choice, held where every caller can reach it — the same shape
   setActiveTimeZone and setActiveDateFormat already use, and for the same
   reason: these are called from components, spreadsheet writers and PDF
   builders alike, and threading a setting through every one of them to reach a
   pure display function is a great deal of plumbing for one preference.

   Set once when the session loads. Anything rendered before that — a login
   screen — uses the default, which is what everything used before this
   existed. */
let activeFormat: TimeFormatId = DEFAULT_TIME_FORMAT;

export function setActiveTimeFormat(format: TimeFormatId | null | undefined): void {
  activeFormat = format === '12h' || format === '24h' ? format : DEFAULT_TIME_FORMAT;
}

export function currentTimeFormat(): TimeFormatId {
  return activeFormat;
}

/**
 * The hour and minute of a moment, in the company's zone and its chosen shape.
 *
 * Takes an ISO timestamp, a Date, or nothing. Nothing comes back as the empty
 * marker rather than as "Invalid Date" — a blank reads as "not recorded", which
 * is what it means.
 *
 * `am`/`pm` in lower case and with no full stops: it sits inside dense table
 * cells beside figures, and "5:18 PM" shouts in a column where nothing else
 * does.
 */
export function formatTimeAs(
  /* A number is accepted because several call sites hold Date.now() or a
     stored epoch, and making each of them wrap it would be four more places to
     get the wrapping wrong. */
  at: string | number | Date | null | undefined,
  format: TimeFormatId = activeFormat,
  empty = '—',
): string {
  if (!at) return empty;

  const when = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(when.getTime())) return empty;

  /* Read out in the company's zone rather than the viewer's. A supervisor
     opening the same screen from another country must see the time the scan
     happened on site, not the time it was in his own evening. */
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: getActiveTimeZone(),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(when);

  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';

  if (format === '24h') return `${String(hour).padStart(2, '0')}:${minute}`;

  /* Built by hand rather than asking Intl for hour12, because Intl's answer
     varies: some locales give "12:05 am" for midnight, some "0:05 am", and at
     least one gives "24:05". Midnight is 12 and noon is 12, and both are
     spelled out here so neither depends on a browser. */
  const suffix = hour < 12 ? 'am' : 'pm';
  const shown = hour % 12 === 0 ? 12 : hour % 12;

  return `${shown}:${minute} ${suffix}`;
}

/**
 * A stored clock time — "17:30", the kind kept in a policy or a shift setting —
 * in the company's shape.
 *
 * Separate from formatTimeAs because there is no moment involved and therefore
 * no zone: "the shift starts at 07:00" means seven in the morning wherever the
 * reader is sitting. Putting it through a Date would attach today's date to it
 * and then convert it, which can move it across midnight.
 */
export function formatClock(
  hhmm: string | null | undefined,
  format: TimeFormatId = activeFormat,
  empty = '—',
): string {
  if (!hhmm) return empty;

  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm.trim());
  if (!m) return hhmm;

  const hour = Number(m[1]);
  const minute = m[2]!;
  if (hour > 23 || Number(minute) > 59) return hhmm;

  if (format === '24h') return `${String(hour).padStart(2, '0')}:${minute}`;

  const suffix = hour < 12 ? 'am' : 'pm';
  const shown = hour % 12 === 0 ? 12 : hour % 12;
  return `${shown}:${minute} ${suffix}`;
}

/** The example beside a format in the settings list. */
export function timeExampleOf(format: TimeFormatId): string {
  return TIME_FORMATS.find((f) => f.id === format)?.example ?? '';
}
